import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Banner, EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { FileUpload } from '@/components/ui/FileUpload';
import { assessmentApi } from '@/features/assessments/api';
import { useAuth } from '@/features/auth/AuthContext';
import { cn } from '@/lib/cn';
import { databaseApi, type IndividualDetail } from './api';
import { PelitaPortfolioTab } from './PelitaPortfolioTab';
import { PelitaIntensiveClassTab } from './PelitaIntensiveClassTab';
import { PelitaCvModal } from './PelitaCvModal';
import { portfolioApi } from './portfolioApi';
import { intensiveClassApi } from './intensiveClassApi';

const TABS = ['Profil', 'Portofolio', 'Kelas Intensif', 'Penilaian', 'Berkas'] as const;
type Tab = (typeof TABS)[number];

/**
 * L-05c (detail) — Profil individu PELITA.
 *
 * Keanggotaan TBM ditampilkan sebagai satu keterangan, bukan linimasa riwayat:
 * program menetapkan 3 orang per TBM yang tetap sama selama 3 tahun penuh,
 * jadi tidak ada perpindahan yang perlu direkam.
 */
export function PelitaDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('Profil');
  const [cvModalOpen, setCvModalOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const canEdit =
    user?.role === 'super_admin' || (user?.role === 'pelita' && user?.individualId === id);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['individual', id],
    queryFn: () => databaseApi.getIndividual(id),
  });

  const { data: portfolios = [] } = useQuery({
    queryKey: ['portfolios', id],
    queryFn: () => portfolioApi.list({ individualId: id }),
    enabled: Boolean(id),
  });

  const { data: assessments = [] } = useQuery({
    queryKey: ['assessments', 'individual', id],
    queryFn: () => assessmentApi.list({ subjectId: id, subjectType: 'individual' }),
    enabled: Boolean(id),
  });

  const { data: intensiveData } = useQuery({
    queryKey: ['intensive-class', 'individual', id],
    queryFn: () => intensiveClassApi.getByIndividual(id),
    enabled: Boolean(id),
  });

  const resetMutation = useMutation({
    mutationFn: () => databaseApi.resetPelitaPassword(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['individual', id] });
      setNotice(`Kata sandi akun ${res.username || data?.code} berhasil direset ke "SLI2026".`);
    },
    onError: (err: Error) => {
      setNotice(`Gagal mereset kata sandi: ${err.message}`);
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-5">
      {notice && (
        <Banner tone="info">
          {notice}
        </Banner>
      )}

      {user?.role !== 'pelita' ? (
        <Link to="/database/pelita" className="text-sm text-ink-secondary hover:text-primary">
          ← Database PELITA SLI
        </Link>
      ) : (
        <span className="text-sm font-semibold text-primary">Profil & Portofolio Diri</span>
      )}

      <Card>
        <div className="flex flex-wrap items-start gap-5">
          {data.photoKey ? (
            <img
              src={data.photoKey.startsWith('http') ? data.photoKey : `/api/v1/files/${data.photoKey}`}
              alt={data.name}
              className="size-16 shrink-0 rounded-full object-cover border border-border shadow-sm"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.nextElementSibling) {
                  (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'grid';
                }
              }}
            />
          ) : null}
          <span
            className={cn(
              'grid size-16 shrink-0 place-items-center rounded-full bg-primary-soft text-xl font-semibold text-primary',
              data.photoKey && 'hidden',
            )}
          >
            {initials(data.name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-2xl font-semibold text-ink">{data.name}</h1>
              <StatusBadge status={data.status} />
            </div>
            <p className="mt-1 text-ink-secondary">
              <span className="tabular font-medium text-ink">{data.code}</span>
              {data.profession && ` · ${data.profession}`}
              {data.lastEducation && ` (${data.lastEducation})`}
              {data.tbmId ? (
                <>
                  {' · '}
                  <Link to={`/database/tbm/${data.tbmId}`} className="text-primary font-medium">
                    {data.tbmName}
                  </Link>
                  {data.regionName && ` · ${data.regionName}`}
                </>
              ) : (
                <span className="ml-2 rounded-full bg-warn-soft px-2.5 py-0.5 text-[13px] text-[#9a6400]">
                  Belum ada TBM
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <Button variant="secondary" onClick={() => setCvModalOpen(true)}>
              📄 Unduh PDF CV
            </Button>
            {canEdit && (
              <Button onClick={() => navigate(`/database/pelita/${id}/ubah`)}>
                Ubah Profil
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'min-h-11 shrink-0 border-b-2 px-4 text-[15px] font-medium transition-colors cursor-pointer',
              tab === t
                ? 'border-primary text-primary'
                : 'border-transparent text-ink-secondary hover:text-ink',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Profil' && (
        <ProfileTab
          individual={data}
          onResetPassword={() => resetMutation.mutate()}
          isResetting={resetMutation.isPending}
        />
      )}
      {tab === 'Portofolio' && <PelitaPortfolioTab individualId={id} />}
      {tab === 'Kelas Intensif' && <PelitaIntensiveClassTab individualId={id} />}
      {tab === 'Penilaian' && <PelitaAssessmentsTab individualId={id} />}
      {tab === 'Berkas' && <PelitaFilesTab individualId={id} photoKey={data.photoKey} />}

      <PelitaCvModal
        open={cvModalOpen}
        onClose={() => setCvModalOpen(false)}
        individual={data}
        portfolios={portfolios}
        assessments={assessments}
        intensiveClass={intensiveData?.record ?? null}
      />
    </div>
  );
}

function ProfileTab({
  individual,
  onResetPassword,
  isResetting,
}: {
  individual: IndividualDetail;
  onResetPassword?: () => void;
  isResetting?: boolean;
}) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const province = individual.regionPath?.find((r) => r.level === 'province');
  const city = individual.regionPath?.find((r) => r.level === 'city');
  const village = individual.regionPath?.find((r) => r.level === 'village');

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Identitas</h2>
        <dl className="flex flex-col gap-3">
          <Row label="Nama" value={individual.name} />
          <Row label="Kode" value={individual.code} />
          <Row label="Personal Branding" value={individual.personalBranding} />
          <Row label="Profesi" value={individual.profession} />
          <Row label="Pendidikan Terakhir" value={individual.lastEducation} />
          <Row
            label="Jenis kelamin"
            value={
              individual.gender === 'L'
                ? 'Laki-laki'
                : individual.gender === 'P'
                  ? 'Perempuan'
                  : null
            }
          />
          <Row label="Tempat lahir" value={individual.birthPlace} />
          <Row label="Tanggal lahir" value={formatDate(individual.birthDate)} />
        </dl>
      </Card>

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Akun Login PELITA SLI</h2>
        <dl className="flex flex-col gap-3">
          <Row label="Username" value={individual.userUsername || individual.code} />
          <Row
            label="Kata Sandi Default"
            value={<code className="rounded bg-subtle px-1.5 py-0.5 font-mono text-sm">SLI2026</code>}
          />
          <Row
            label="Status Akun"
            value={
              individual.userIsActive !== false ? (
                <span className="font-semibold text-success">Aktif</span>
              ) : (
                <span className="font-semibold text-danger">Nonaktif</span>
              )
            }
          />
        </dl>
        <div className="mt-4 pt-3 border-t border-border flex flex-col gap-2">
          {isSuperAdmin ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-xs text-ink-secondary">
                Atur ulang kata sandi anggota ini ke kata sandi bawaan (<strong>SLI2026</strong>).
              </p>
              <Button
                size="sm"
                variant="secondary"
                loading={isResetting}
                onClick={onResetPassword}
              >
                Reset Kata Sandi ke SLI2026
              </Button>
            </div>
          ) : (
            <p className="text-xs text-ink-secondary">
              Gunakan username di atas untuk masuk ke sistem. Jika lupa kata sandi, konfirmasi admin untuk meminta reset kata sandi ke bawaan.
            </p>
          )}
        </div>
      </Card>

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Keanggotaan TBM</h2>
        <dl className="flex flex-col gap-3">
          <Row
            label="TBM"
            value={individual.tbmId ? `${individual.tbmCode} — ${individual.tbmName}` : null}
          />
          <Row label="Desa / Nagari" value={village?.name ?? individual.regionName} />
          <Row label="Kabupaten / Kota" value={city?.name} />
          <Row label="Provinsi" value={province?.name} />
        </dl>
        {!individual.tbmId && (
          <p className="mt-4 rounded-[var(--radius-input)] bg-warn-soft px-3.5 py-2.5 text-[13px] text-[#9a6400]">
            Orang ini belum terhubung ke TBM mana pun — kemungkinan TBM lamanya dihapus. Klik
            <strong> Ubah</strong> untuk memilih TBM barunya. Selama kosong, assessor tidak bisa
            melihat datanya.
          </p>
        )}
      </Card>

      <Card>
        <h2 className="mb-4 text-[17px] font-semibold">Kontak</h2>
        <dl className="flex flex-col gap-3">
          <Row label="No. HP" value={individual.phone} />
          <Row label="Email" value={individual.email} />
        </dl>
        <p className="mt-4 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 text-[13px] text-ink-secondary">
          Data kontak hanya terlihat oleh Super Admin dan assessor yang mendampingi TBM
          bersangkutan.
        </p>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 text-[15px]">
      <dt className="text-ink-secondary">{label}</dt>
      <dd className={value ? 'text-ink' : 'text-ink-muted'}>{value || 'Belum diisi'}</dd>
    </div>
  );
}

/** ISO (2026-08-16) → "16 Agustus 2026". Kolomnya teks, jadi tanpa zona waktu. */
function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const bulan = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
  ];
  return `${d} ${bulan[m - 1]} ${y}`;
}

function initials(name: string): string {
  return name
    .replace(/^\[Contoh\]\s*/, '')
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function PelitaAssessmentsTab({ individualId }: { individualId: string }) {
  const navigate = useNavigate();
  const { data: rows, isLoading, error, refetch } = useQuery({
    queryKey: ['assessments', 'individual', individualId],
    queryFn: () => assessmentApi.list({ subjectId: individualId, subjectType: 'individual' }),
  });

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!rows || rows.length === 0) {
    return (
      <Card padded={false}>
        <EmptyState
          icon="📝"
          title="Belum ada riwayat asesmen kompetensi"
          description="Asesmen kompetensi untuk individu ini akan muncul di sini setelah dinilai dari menu Pengukuran › Kompetensi PELITA SLI."
        />
      </Card>
    );
  }

  return (
    <Card padded={false} className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <thead>
          <tr className="border-b border-border text-[13px] font-medium text-ink-secondary">
            <th className="px-4 py-3">Periode</th>
            <th className="px-4 py-3">Instrumen</th>
            <th className="px-4 py-3">Angkatan</th>
            <th className="px-4 py-3">Indeks</th>
            <th className="px-4 py-3">Kategori</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-border text-sm last:border-0 hover:bg-app/50">
              <td className="px-4 py-3 font-medium text-ink">{row.periodName}</td>
              <td className="px-4 py-3 text-ink-secondary">{row.instrumentName}</td>
              <td className="px-4 py-3 tabular">{row.angkatan ?? '—'}</td>
              <td className="px-4 py-3 tabular font-semibold text-ink">
                {row.totalScore === null ? '—' : Math.round(row.totalScore * 100) / 100}
              </td>
              <td className="px-4 py-3">{row.band ?? <span className="text-ink-muted">—</span>}</td>
              <td className="px-4 py-3">
                <StatusBadge status={row.status} />
              </td>
              <td className="px-4 py-3 text-right">
                <Button size="sm" variant="secondary" onClick={() => navigate(`/pengukuran/${row.id}`)}>
                  Buka Penilaian
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function PelitaFilesTab({ individualId, photoKey }: { individualId: string; photoKey: string | null }) {
  return (
    <div className="flex flex-col gap-4">
      {photoKey && (
        <Card>
          <h2 className="mb-3 text-[17px] font-semibold">Foto Profil</h2>
          <div className="flex items-center gap-4">
            <img
              src={photoKey.startsWith('http') ? photoKey : `/api/v1/files/${photoKey}`}
              alt="Foto Profil"
              className="size-16 rounded-full object-cover border border-border shadow-sm"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <p className="text-sm text-ink-secondary">
              Foto profil resmi anggota PELITA SLI. Untuk mengganti foto, gunakan tombol Ubah di bagian atas.
            </p>
          </div>
        </Card>
      )}

      <Card>
        <h2 className="mb-2 text-[17px] font-semibold">Portofolio & Berkas Pendukung</h2>
        <p className="mb-4 text-sm text-ink-secondary">
          Unggah dokumen portofolio, sertifikat pelatihan, CV, atau karya literasi individu ini (PDF, JPG, PNG, WebP maks 5 MB).
        </p>
        <FileUpload
          entityType="pelita_document"
          entityId={individualId}
          label="Daftar Dokumen PELITA SLI"
        />
      </Card>
    </div>
  );
}
